import {
  DefaultButton,
  Dropdown,
  FocusTrapZone,
  FocusZone,
  GroupedList,
  IDropdownOption,
  IGroup,
  Label,
  Link,
  PrimaryButton,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Stack,
  TextField
} from "@fluentui/react";
import { DetailsRow, IColumn } from "@fluentui/react/lib/DetailsList";
import { Selection, SelectionZone } from "@fluentui/react/lib/Selection";
import { FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import * as Yup from "yup";

import { Contract } from "../../contract.interface";

import { PersonalizeOfferModal } from "./personalizeOfferModal";

import { AdditionalOfferPriceToAppend } from "@/components/Offers/AdditionalComponents/AdditionalOfferPriceToAppend";
import { IAdditionalOfferPrice } from "@/components/Settings/AdditionalComponents/types";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { SubmittingDiv } from "@/modules/Contracts/styles";
import { OfferPrice } from "@/modules/Offers/types/ProductsOffers.types";
import { ENERGY_PROVIDER_OPTIONS } from "@/modules/Settings/constants/constants";
import {
  LineStrategyType,
  OperationType
} from "@/modules/Settings/pages/Operations/enum";
import { formatToSelect } from "@/utils/FormatToSelect/format-to-select";
import { toLocaleString } from "@/utils/ToLocaleString";

interface EnergyItemProps {
  contract: Contract;
  close: (event?: unknown, refreshCondition?: boolean) => void;
}

interface Offer {
  id: number;
  discount: number;
  activationFee: string;
  combo: string;
  offerPrice: OfferPrice;
}

interface Plan {
  id: number;
  name: string;
  offers: Array<Offer>;
}

interface Carrier {
  id: number;
  name: string;
  logo: string;
  status: boolean;
  plans: Array<Plan>;
}

export interface AvailableOffer {
  id: number;
  name: string;
  lineStrategy: LineStrategyType;
  type: OperationType;
  carriers: Array<Carrier>;
}

interface FormValues {
  operationId: number | null;
  carrierId: number | null;
  energyInstallationNumber: string;
  energyProvider: string;
  selectedOfferPriceId: number | null;
}

interface MappedOffer {
  key: number;
  description: string;
  details: Offer;
  plan: number;
}

const ComboDisplayNames: Record<string, string> = {
  LIGHT: "LIGHT",
  LIGHT_DADOS: "LIGHT DADOS",
  CONECTIVIDADE_BASICA: "CONECTIVIDADE BÁSICA",
  CONECTIVIDADE_AVANCADA: "CONECTIVIDADE AVANÇADA",
  SMART: "SMART",
  TAG: "TAG"
};

const validationSchema = Yup.object({
  operationId: Yup.number().nullable().required("Selecione uma operação"),
  carrierId: Yup.number().nullable().required("Selecione uma operadora"),
  energyInstallationNumber: Yup.string()
    .required("Instalação de ativação de energia é obrigatória")
    .trim(),
  energyProvider: Yup.string()
    .required("Selecione uma concessionária local")
    .trim(),
  selectedOfferPriceId: Yup.number()
    .nullable()
    .required("Selecione um benefício")
});

const initialValues: FormValues = {
  operationId: null,
  carrierId: null,
  energyInstallationNumber: "",
  energyProvider: "",
  selectedOfferPriceId: null
};

const columns: Array<IColumn> = [
  {
    key: "description",
    name: "Descrição",
    fieldName: "description",
    minWidth: 280
  }
];

const Loading = (): JSX.Element => (
  <FocusTrapZone>
    <FocusZone>
      <SubmittingDiv>
        <Spinner size={SpinnerSize.large} />
      </SubmittingDiv>
    </FocusZone>
  </FocusTrapZone>
);

export function EnergyItem({ contract, close }: EnergyItemProps): JSX.Element {
  const service: number = contract.service.id;
  const businessActionId: number = contract.businessUnit.id;

  const [personalizeOfferModalOpen, setPersonalizeOfferModalOpen] =
    useState(false);
  const [monthlyDiscount, setMonthlyDiscount] = useState<number | null>(null);

  const [groups, setGroups] = useState<Array<IGroup>>([]);
  const [mappedOffers, setMappedOffers] = useState<Array<MappedOffer>>([]);

  const [selectedOffer, setSelectedOffer] = useState<MappedOffer | null>(null);
  const [isAdditionalModalOpen, setIsAdditionalModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { execute } = useApi();

  const { data: availableOffers, isFetching } = useGet<Array<AvailableOffer>>({
    queryToken: `contract-${contract.id}-available-offers-${service}`,
    url: `contracts/offers`,
    params: { service, businessActionId }
  });

  const handleFinalSubmit = useCallback(
    async (
      additionalOfferPrice: Array<IAdditionalOfferPrice>,
      formValues: FormValues
    ): Promise<void> => {
      if (!selectedOffer) return;

      setIsCreating(true);

      const payload = {
        operation: formValues.operationId,
        offerPrice: formValues.selectedOfferPriceId,
        plan: selectedOffer.plan,
        additionalInfo: {
          planIsConectivityOrSmart: false,
          energyInstallationNumber: formValues.energyInstallationNumber,
          energyProvider: formValues.energyProvider
        },
        additionalContractItems: additionalOfferPrice,
        offerOverride: monthlyDiscount ? { discount: monthlyDiscount } : null
      };

      const response = await execute({
        url: `contracts/${contract.id}/item?businessActionId=${businessActionId}`,
        method: "POST",
        bodyObj: payload,
        showAlert: true,
        messages: {
          success: "Item adicionado com sucesso!",
          error: "Erro ao adicionar item ao contrato."
        },
        setIsFetching: setIsCreating
      });

      if (response?.response?.ok === true) {
        setIsAdditionalModalOpen(false);
        close(null, true);
      }
    },
    [
      selectedOffer,
      monthlyDiscount,
      contract.id,
      businessActionId,
      execute,
      close
    ]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false);

      if (
        selectedOffer?.details?.offerPrice?.additionalOfferPrice?.length > 0
      ) {
        setIsAdditionalModalOpen(true);
      } else {
        handleFinalSubmit([], values).catch(console.error);
      }
    }
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    handleSubmit
  } = formik;

  const selection = useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          const selectedItem = selection.getSelection()[0] as MappedOffer;

          if (selectedItem) {
            setFieldValue("selectedOfferPriceId", selectedItem.key).catch(
              console.error
            );
            setSelectedOffer(selectedItem);
          } else {
            setFieldValue("selectedOfferPriceId", null).catch(console.error);
            setSelectedOffer(null);
          }
        },
        selectionMode: SelectionMode.single
      }),
    [setFieldValue]
  );

  const mapOfferToDetails = (offer: Offer, planId: number): MappedOffer => {
    const { offerPrice, combo, discount } = offer;

    const comboType = ComboDisplayNames[combo] || combo;
    const offerPriceStr = toLocaleString(offerPrice?.price);
    const discountStr = discount
      ? ` com desconto de ${toLocaleString(discount)} por mês`
      : "";
    const description = `Combo ${comboType} (${offerPriceStr}/mês${discountStr})`;

    return {
      key: offerPrice?.id,
      description,
      details: offer,
      plan: planId
    };
  };

  useEffect(() => {
    setMappedOffers([]);
    setGroups([]);
    setFieldValue("selectedOfferPriceId", null).catch(console.error);
    setSelectedOffer(null);

    if (!values.operationId || !values.carrierId || !availableOffers) return;

    const selectedOperation = availableOffers.find(
      op => op.id === values.operationId
    );
    const selectedCarrier = selectedOperation?.carriers?.find(
      carrier => carrier.id === values.carrierId
    );
    const plans = selectedCarrier?.plans || [];

    if (plans.length > 0) {
      const newGroups: Array<IGroup> = [];
      const newOffers: Array<MappedOffer> = [];

      plans.forEach(plan => {
        newGroups.push({
          count: plan.offers?.length || 0,
          key: String(plan.id),
          name: plan.name,
          startIndex: newOffers.length,
          level: 1,
          isCollapsed: true
        });

        plan.offers?.forEach(offer => {
          newOffers.push(mapOfferToDetails(offer, plan.id));
        });
      });

      setGroups(newGroups);
      setMappedOffers(newOffers);
      selection.setItems(newOffers, true);
    }
  }, [
    values.operationId,
    values.carrierId,
    availableOffers,
    setFieldValue,
    selection
  ]);

  const handleSaveCustomization = (discount: number): void => {
    setMonthlyDiscount(discount);
    setPersonalizeOfferModalOpen(false);
  };

  const handleCancelCustomization = (): void => {
    setPersonalizeOfferModalOpen(false);
  };

  const getCarrierOptions = (
    selectedOperationId: number | null
  ): Array<IDropdownOption> => {
    if (!selectedOperationId || !availableOffers) return [];

    const selectedOperation = availableOffers.find(
      offer => offer.id === selectedOperationId
    );

    return formatToSelect(selectedOperation?.carriers || []);
  };

  const onRenderCell = (
    nestingDepth: number,
    item: MappedOffer,
    itemIndex: number
  ): JSX.Element => {
    return (
      <DetailsRow
        id={`energy-plan-${itemIndex}-select`}
        columns={columns}
        groupNestingDepth={nestingDepth}
        item={item}
        itemIndex={itemIndex}
        selection={selection}
        selectionMode={SelectionMode.single}
        indentWidth={1}
        styles={{
          root: {
            width: "100%",
            selectors: {
              "& .ms-DetailsRow-check": {
                opacity: 1
              }
            }
          },
          fields: {
            width: "calc(100% - 48px)"
          },
          cell: {
            whiteSpace: "pre-wrap"
          }
        }}
      />
    );
  };

  const getFieldError = (fieldName: keyof FormValues): string | undefined => {
    return touched[fieldName] && errors[fieldName]
      ? errors[fieldName]
      : undefined;
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    handleSubmit();
  };

  const handleOperationChange = (
    _: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption
  ): void => {
    setFieldValue("operationId", option?.key ?? null).catch(console.error);
    setFieldValue("carrierId", null).catch(console.error);
  };

  const handleCarrierChange = (
    _: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption
  ): void => {
    setFieldValue("carrierId", option?.key ?? null).catch(console.error);
  };

  const handleEnergyInstallationChange = (
    _: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ): void => {
    setFieldValue("energyInstallationNumber", newValue ?? "").catch(
      console.error
    );
  };

  const handleEnergyProviderChange = (
    _: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption
  ): void => {
    setFieldValue("energyProvider", option?.key ?? "").catch(console.error);
  };

  const handleOperationBlur = (): void => {
    setFieldTouched("operationId", true).catch(console.error);
  };

  const handleCarrierBlur = (): void => {
    setFieldTouched("carrierId", true).catch(console.error);
  };

  const handleEnergyInstallationBlur = (): void => {
    setFieldTouched("energyInstallationNumber", true).catch(console.error);
  };

  const handleEnergyProviderBlur = (): void => {
    setFieldTouched("energyProvider", true).catch(console.error);
  };

  const handleOpenPersonalizeModal = (): void => {
    setPersonalizeOfferModalOpen(true);
  };

  const handleCancel = (): void => {
    close();
  };

  const handleAdditionalSubmit = (
    additionalOfferPrice: Array<IAdditionalOfferPrice>
  ): void => {
    handleFinalSubmit(additionalOfferPrice, formik.values).catch(console.error);
  };

  return isFetching ? (
    <Loading />
  ) : (
    <Fragment>
      <PersonalizeOfferModal
        isOpen={personalizeOfferModalOpen}
        onDismiss={handleCancelCustomization}
        onSave={handleSaveCustomization}
      />
      <FormikProvider value={formik}>
        <form onSubmit={handleFormSubmit}>
          <Dropdown
            id="select-contract-operation"
            label="Selecione uma operação"
            placeholder="Selecione uma operação"
            required
            selectedKey={values.operationId}
            options={formatToSelect(availableOffers || [])}
            onChange={handleOperationChange}
            onBlur={handleOperationBlur}
            errorMessage={getFieldError("operationId")}
            styles={{ root: { marginBottom: 20 } }}
          />

          <Dropdown
            id="select-contract-carrier"
            label="Selecione uma operadora"
            placeholder="Selecione uma operadora"
            required
            selectedKey={values.carrierId}
            options={getCarrierOptions(values.operationId)}
            onChange={handleCarrierChange}
            onBlur={handleCarrierBlur}
            disabled={!values.operationId}
            errorMessage={getFieldError("carrierId")}
            styles={{ root: { marginBottom: 20 } }}
          />

          {values.carrierId && (
            <div style={{ marginBottom: 20 }}>
              <Label required>Selecione um benefício</Label>
              <div
                style={{
                  border: getFieldError("selectedOfferPriceId")
                    ? "1px solid #a4262c"
                    : "1px solid",
                  borderRadius: "2px"
                }}
              >
                {mappedOffers.length > 0 && groups.length > 0 ? (
                  <SelectionZone
                    selection={selection}
                    selectionMode={SelectionMode.single}
                    selectionPreservedOnEmptyClick
                  >
                    <GroupedList
                      items={mappedOffers}
                      onRenderCell={onRenderCell}
                      selection={selection}
                      selectionMode={SelectionMode.single}
                      groups={groups}
                      compact
                      groupProps={{
                        headerProps: {
                          indentWidth: 1
                        }
                      }}
                    />
                  </SelectionZone>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      height: "300px",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#605e5c",
                      textAlign: "center",
                      padding: "0 16px"
                    }}
                  >
                    Nenhum benefício encontrado para a operadora selecionada.
                  </div>
                )}
              </div>
              {getFieldError("selectedOfferPriceId") && (
                <span style={{ color: "#a4262c", fontSize: 12, marginTop: 4 }}>
                  {getFieldError("selectedOfferPriceId")}
                </span>
              )}

              <Dropdown
                id="energy-provider"
                label="Concessionária local"
                placeholder="Selecione a concessionária local"
                required
                selectedKey={values.energyProvider}
                options={ENERGY_PROVIDER_OPTIONS}
                onChange={handleEnergyProviderChange}
                onBlur={handleEnergyProviderBlur}
                errorMessage={getFieldError("energyProvider")}
                styles={{ root: { marginBottom: 20 } }}
              />

              <TextField
                id="energy-installation-number"
                label="Unidade Consumidora"
                placeholder="Unidade Consumidora"
                required
                value={values.energyInstallationNumber}
                onChange={handleEnergyInstallationChange}
                onBlur={handleEnergyInstallationBlur}
                errorMessage={getFieldError("energyInstallationNumber")}
                styles={{ root: { marginBottom: 20 } }}
              />
            </div>
          )}

          <Link onClick={handleOpenPersonalizeModal}>Personalizar</Link>

          {monthlyDiscount !== null && (
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
              styles={{ root: { marginTop: 8 } }}
            >
              <span>
                Desconto mensal:{" "}
                <strong>
                  R$ {monthlyDiscount.toFixed(2).replace(".", ",")}
                </strong>
              </span>
            </Stack>
          )}

          <Stack
            horizontal
            style={{ maxHeight: 200, marginTop: 20 }}
            tokens={{ childrenGap: 8 }}
          >
            <DefaultButton
              id="button-cancel"
              onClick={handleCancel}
              style={{ padding: "8px 16px" }}
              disabled={isCreating}
            >
              Cancelar
            </DefaultButton>

            <PrimaryButton
              id="submit-button"
              type="submit"
              style={{ padding: "8px 16px" }}
              disabled={isCreating}
            >
              Adicionar
            </PrimaryButton>
          </Stack>
        </form>

        {selectedOffer?.details?.offerPrice?.additionalOfferPrice && (
          <AdditionalOfferPriceToAppend
            additionalOfferPriceList={
              selectedOffer.details.offerPrice.additionalOfferPrice
            }
            isModalOpen={isAdditionalModalOpen}
            setIsModalOpen={setIsAdditionalModalOpen}
            submit={handleAdditionalSubmit}
            isSubmitting={isCreating}
            submitMessage={""}
            clearSubmitMessage={() => {}}
            canEdit={true}
            planPrice={parseFloat(
              selectedOffer.details.offerPrice.price?.replace(",", ".") || "0"
            )}
          />
        )}
      </FormikProvider>
    </Fragment>
  );
}
